import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import useSWR from 'swr'
import CreatableSelect from 'react-select/creatable'
import { fetchVodAsset, updateVodAsset, fetchVodAssetTags, updateVodAssetTags, fetchVodTags, createVodTag } from '../services/vodApi'
import { fetchMatches } from '../services/api'
import { Button, Panel, Badge, ImageManagement } from '../components'
import { formatToPacificTime } from '../utils/timezone'
import selectStyles from '../styles/react-select.module.scss'
import pageStyles from '../styles/page.module.scss'
import styles from './VodHighlightDetail.module.scss'
import { VodPublishStatusCode } from '../types/vod'

const VodHighlightDetail: React.FC = () => {
  const { highlightId } = useParams<{ highlightId: string }>()
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [synopsis, setSynopsis] = useState<string>('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [tagOptions, setTagOptions] = useState<Array<{ value: string; label: string }>>([])
  const [isUpdatingTags, setIsUpdatingTags] = useState(false)
  const [tagError, setTagError] = useState<string | null>(null)

  // Fetch VOD asset
  const { data: asset, isLoading, error, mutate } = useSWR(
    highlightId ? ['vod-asset', highlightId] : null,
    () => fetchVodAsset(highlightId!),
    { revalidateOnFocus: false }
  )

  // Fetch full match data if match is linked (using ext_event_id from asset.match)
  const matchTcId = asset?.match?.tc_match_id
  const matchStartTime = asset?.match?.tc_start_time
  const { data: matchResponse, isLoading: matchLoading } = useSWR(
    matchTcId ? ['match-by-tc-id', matchTcId, matchStartTime] : null,
    async () => {
      if (!matchTcId) return null
      // Use a date range around the match start time if available
      let startDate: string
      let endDate: string
      if (matchStartTime) {
        const date = new Date(matchStartTime)
        const start = new Date(date)
        start.setDate(start.getDate() - 30)
        const end = new Date(date)
        end.setDate(end.getDate() + 30)
        startDate = start.toISOString().split('T')[0]
        endDate = end.toISOString().split('T')[0]
      } else {
        const today = new Date()
        const start = new Date(today)
        start.setFullYear(start.getFullYear() - 1)
        const end = new Date(today)
        end.setFullYear(end.getFullYear() + 1)
        startDate = start.toISOString().split('T')[0]
        endDate = end.toISOString().split('T')[0]
      }
      const response = await fetchMatches({
        start_date: startDate,
        end_date: endDate,
        search: matchTcId,
        limit: 1,
        page: 1
      })
      return response.matches?.[0] || null
    },
    { revalidateOnFocus: false }
  )

  const match = matchResponse || null

  // Initialize form fields when asset loads
  React.useEffect(() => {
    if (asset) {
      setTitle(asset.tc_title || '')
      setDescription(asset.tc_description || '')
      setSynopsis(asset.tc_synopsis || '')
    }
  }, [asset])

  // Fetch available tags and current asset tags on mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        // Fetch all available tags for dropdown
        const allTags = await fetchVodTags()
        setTagOptions(
          allTags.tags.map(t => ({ 
            value: t.value,  // tag_key
            label: t.label   // display_name or tag_key
          }))
        )

        // Fetch current asset tags if asset is loaded
        if (highlightId && asset) {
          const assetTags = await fetchVodAssetTags(highlightId)
          setSelectedTags(assetTags.tags || [])
        }
      } catch (err) {
        console.error('Failed to fetch tags:', err)
        setTagError('Failed to load tags')
      }
    }

    if (asset && highlightId) {
      fetchTags()
    }
  }, [asset, highlightId])

  // Check if any editable fields have changed
  const hasChanges = useMemo(() => {
    if (!asset) return false
    return (
      title !== (asset.tc_title || '') ||
      description !== (asset.tc_description || '') ||
      synopsis !== (asset.tc_synopsis || '')
    )
  }, [title, description, synopsis, asset])

  // Check if form is valid (required fields are filled)
  const isValid = useMemo(() => {
    return title.trim().length > 0 && description.trim().length > 0
  }, [title, description])

  // Format duration (seconds to MM:SS)
  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Get status badge variant
  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'ACTIVE':
        return 'success'
      case 'EXPIRED':
        return 'error'
      case 'HIDDEN':
        return 'warning'
      default:
        return 'warning'
    }
  }

  // Format status for display
  const formatStatus = (status: string): string => {
    switch (status) {
      case 'ACTIVE':
        return 'Active'
      case 'EXPIRED':
        return 'Expired'
      case 'HIDDEN':
        return 'Hidden'
      default:
        return status
    }
  }

  // Get publish status badge variant
  const getPublishStatusVariant = (code: VodPublishStatusCode): 'success' | 'warning' | 'error' => {
    switch (code) {
      case 'PUBLISHED_CURRENT':
      case 'PUBLISHED_OUT_OF_DATE': // Same as PUBLISHED_CURRENT because we don't require a publish after vod asset ingestion
        return 'success'
      case 'VIRTUAL_ONLY':
      case 'NO_ACTIVE_PUB_CONTENT':
      case 'NO_PUBLISH_MESSAGE':
        return 'warning'
      case 'UNLINKED_NO_MATCH':
      default:
        return 'error'
    }
  }

  // Format status for display
  const formatPublishStatus = (code: VodPublishStatusCode): string => {
    switch (code) {
      case 'PUBLISHED_CURRENT':
      case 'PUBLISHED_OUT_OF_DATE': // Same as PUBLISHED_CURRENT because we don't require a publish after vod asset ingestion
        return 'Current'
      case 'VIRTUAL_ONLY':
        return 'Virtual'
      case 'NO_ACTIVE_PUB_CONTENT':
      case 'NO_PUBLISH_MESSAGE':
        return 'Not published'
      case 'UNLINKED_NO_MATCH':
        return 'Unlinked'
      default:
        return code
    }
  }

  // Construct S3 URL from bucket/key (fallback if CDN URL not available)
  const constructS3Url = (bucket: string, key: string): string => {
    // Remove leading slash from key if present
    const cleanKey = key.startsWith('/') ? key.slice(1) : key
    return `https://s3.amazonaws.com/${bucket}/${cleanKey}`
  }

  // Get video URL (prefer CDN, fallback to S3)
  const getVideoUrl = (): string | null => {
    if (!asset?.video) return null
    if (asset.video.cdn_url) return asset.video.cdn_url
    if (asset.video.s3_bucket && asset.video.s3_key) {
      return constructS3Url(asset.video.s3_bucket, asset.video.s3_key)
    }
    return null
  }


  const handleSave = async () => {
    if (!highlightId || !asset) return

    // Validate required fields
    if (!title.trim()) {
      setSaveError('Title is required')
      return
    }
    if (!description.trim()) {
      setSaveError('Description is required')
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      await updateVodAsset(highlightId, {
        tc_title: title.trim(),
        tc_description: description.trim(),
        tc_synopsis: synopsis.trim() || undefined
      })
      await mutate()
    } catch (err) {
      setSaveError((err as Error).message || 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = useCallback(() => {
    if (asset) {
      setTitle(asset.tc_title || '')
      setDescription(asset.tc_description || '')
      setSynopsis(asset.tc_synopsis || '')
    }
    setSaveError(null)
  }, [asset])

  const handleUpdateTags = useCallback(async (tags: string[]) => {
    if (!highlightId) return
    
    setIsUpdatingTags(true)
    setTagError(null)
    
    try {
      await updateVodAssetTags(highlightId, tags)
      setSelectedTags(tags)
      // Refresh asset data to get updated tags
      await mutate()
    } catch (err: any) {
      setTagError(err.message || 'Failed to update tags')
    } finally {
      setIsUpdatingTags(false)
    }
  }, [highlightId, mutate])

  const handleCreateTag = useCallback(async (tagKey: string) => {
    try {
      const response = await createVodTag(tagKey)
      // Refresh tag options to include new tag
      const tags = await fetchVodTags()
      setTagOptions(
        tags.tags.map(t => ({ value: t.value, label: t.label }))
      )
      return response.tag
    } catch (err: any) {
      setTagError(err.message || 'Failed to create tag')
      throw err
    }
  }, [])

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading highlight</h2>
          <p>Failed to fetch highlight details: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  if (isLoading || !asset) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading highlight...</h3>
          <p>Please wait while we fetch the highlight data.</p>
        </div>
      </div>
    )
  }

  const videoUrl = getVideoUrl()

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1>Highlight & Condensed Match Details</h1>
        <p>
          <code className={styles.idCode}>{asset.tc_vod_asset_id}</code>
        </p>
      </div>

      {saveError && (
        <div className={pageStyles.error}>
          <p>Error: {saveError}</p>
        </div>
      )}

      <div className={styles.detailContent}>
        {/* Left Column: VOD Metadata */}
        <div className={styles.leftColumn}>
          <Panel>
            <Panel.Header>
              <h2>VOD Metadata</h2>
            </Panel.Header>
            <div className={styles.fieldGrid}>
              {/* Read-only fields */}
              <div className={styles.fieldGroup}>
                <label>Asset ID</label>
                <input
                  type="text"
                  value={asset.tc_vod_asset_id}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>VOD Type</label>
                <input
                  type="text"
                  value={asset.vod_type_display_name || 'N/A'}
                  disabled
                  className={styles.input}
                />
              </div>

              {/* Editable: Title */}
              <div className={styles.fieldGroup}>
                <label>
                  Title <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={styles.input}
                />
              </div>

              {/* Editable: Description */}
              <div className={styles.fieldGroup}>
                <label>
                  Description <span className={styles.required}>*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              {/* Editable: Synopsis */}
              <div className={styles.fieldGroup}>
                <label>Synopsis</label>
                <textarea
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Tags</label>
                {tagError && (
                  <div className={styles.errorText}>
                    {tagError}
                  </div>
                )}
                <CreatableSelect
                  isMulti
                  options={tagOptions}
                  value={selectedTags.map(tag => {
                    const option = tagOptions.find(opt => opt.value === tag)
                    return { value: tag, label: option?.label || tag }
                  })}
                  onChange={(selectedOptions) => {
                    const tags = selectedOptions ? selectedOptions.map(opt => opt.value) : []
                    handleUpdateTags(tags)
                  }}
                  onCreateOption={async (inputValue) => {
                    try {
                      await handleCreateTag(inputValue)
                      // Add the new tag to selected tags
                      handleUpdateTags([...selectedTags, inputValue])
                    } catch (err) {
                      // Error already handled in handleCreateTag
                    }
                  }}
                  isDisabled={isUpdatingTags || isLoading}
                  className={selectStyles.reactSelect}
                  classNamePrefix="react-select"
                  placeholder="Type to create or select tags..."
                  formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Sport</label>
                <input
                  type="text"
                  value={asset.sport?.tc_sport_name || 'N/A'}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Match SR ID</label>
                <input
                  type="text"
                  value={asset.match?.ext_event_id || 'N/A'}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Player SR IDs</label>
                <div className={styles.tagsContainer}>
                  {asset.players && asset.players.length > 0 ? (
                    asset.players.map((player, idx) => (
                      <code key={idx} className={styles.srIdCode}>{player.ext_competitor_id}</code>
                    ))
                  ) : (
                    <span className={styles.emptyText}>No player IDs</span>
                  )}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label>Status</label>
                <div>
                  <Badge variant={getStatusVariant(asset.status)}>
                    {formatStatus(asset.status)}
                  </Badge>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label>Publish Status</label>
                <div>
                  <Badge variant={getPublishStatusVariant(asset.publish_status)}>
                    {formatPublishStatus(asset.publish_status)}
                  </Badge>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label>Created At</label>
                <input
                  type="text"
                  value={formatToPacificTime(asset.created_at)}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Updated At</label>
                <input
                  type="text"
                  value={formatToPacificTime(asset.updated_at)}
                  disabled
                  className={styles.input}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <Button
                onClick={handleSave}
                size="large"
                disabled={isSaving || !hasChanges || !isValid}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              {hasChanges && (
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Reset Changes
                </Button>
              )}
            </div>
          </Panel>
        </div>

        {/* Right Column: Match Info & Assets */}
        <div className={styles.rightColumn}>
          {/* Match Information Panel */}
          {matchTcId && (
            <Panel variant="no-margin">
              <Panel.Header>
                <h2>Related Match</h2>
              </Panel.Header>
              {matchLoading ? (
                <div className={styles.loadingState}>
                  <p>Loading match data...</p>
                </div>
              ) : match ? (
                <div className={styles.matchInfo}>
                  <div className={styles.matchRow}>
                    <strong>Players:</strong>
                    <span>
                      {match.home_competitor?.name || 'TBD'} vs{' '}
                      {match.away_competitor?.name || 'TBD'}
                    </span>
                  </div>
                  {match.competition && (
                    <div className={styles.matchRow}>
                      <strong>Tournament:</strong>
                      <span>{match.competition.name}</span>
                    </div>
                  )}
                  {match.match.effective_round ? (
                    <div className={styles.matchRow}>
                      <strong>Round:</strong>
                      <span>{match.match.effective_round}</span>
                    </div>
                  ) : null}
                  {match.match.effective_start_time ? (
                    <div className={styles.matchRow}>
                      <strong>Match Date/Time:</strong>
                      <span>{formatToPacificTime(match.match.effective_start_time)}</span>
                    </div>
                  ) : null}
                  {match.match.effective_status ? (
                    <div className={styles.matchRow}>
                      <strong>Match Status:</strong>
                      <span>{match.match.effective_status}</span>
                    </div>
                  ) : null}
                  {match.match.tc_match_id && (
                    <div className={styles.matchRow}>
                      <Link to={`/matches/${match.match.tc_match_id}`} className={styles.matchLink}>
                        View Match Details →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.errorState}>
                  <p>Match not found for SR ID: <code>{asset?.match?.ext_event_id ?? 'N/A'}</code> or TC ID: <code>{matchTcId ?? 'N/A'}</code></p>
                  <p className={styles.errorHint}>Match data may not be available yet, or SR ID may be invalid.</p>
                </div>
              )}
            </Panel>
          )}

          {/* Video Asset Panel */}
          {asset.video && (
            <Panel variant="no-margin">
              <Panel.Header>
                <h2>Video Asset</h2>
              </Panel.Header>
              <div className={styles.assetInfo}>
                {asset.video.s3_bucket && (
                  <div className={styles.assetRow}>
                    <strong>Bucket:</strong>
                    <code className={styles.codeValue}>{asset.video.s3_bucket}</code>
                  </div>
                )}
                {asset.video.s3_key && (
                  <div className={styles.assetRow}>
                    <strong>Key:</strong>
                    <code className={styles.codeValue}>{asset.video.s3_key}</code>
                  </div>
                )}
                <div className={styles.assetRow}>
                  <strong>Duration:</strong>
                  <span>{formatDuration(asset.duration_sec)}</span>
                </div>
                {asset.video.mime_type && (
                  <div className={styles.assetRow}>
                    <strong>MIME Type:</strong>
                    <span>{asset.video.mime_type}</span>
                  </div>
                )}
                {videoUrl && (
                  <div className={styles.assetRow}>
                    <strong>Video URL:</strong>
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.urlLink}
                    >
                      {videoUrl}
                    </a>
                  </div>
                )}
              </div>
            </Panel>
          )}

          {/* Thumbnails Panel */}
          <Panel variant="no-margin">
            <Panel.Header>
              <h2>Thumbnails</h2>
            </Panel.Header>
            <ImageManagement 
              entityType="vod" 
              entityId={highlightId!}
              defaultImageType={
                asset.vod_type_code === 'HIGHLIGHT' 
                  ? 'highlight_card' 
                  : asset.vod_type_code === 'CONDENSED' 
                    ? 'condensed_card' 
                    : undefined
              }
            />
          </Panel>
        </div>
      </div>
    </div>
  )
}

export default VodHighlightDetail

